import {
  Autocomplete,
  TextField,
  Checkbox,
  FormHelperText,
  IconButton,
  Icon,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import PropTypes from "prop-types";
import { get, isEqual, debounce } from "lodash";
import { useTranslation } from "react-i18next";
import "./BMXAsynchronousMulti.scss";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function BMXAsynchronousMulti(props) {
  const {
    meta: { touched, error, submitError },
    input,
    currentValues,
    getData,
    afterIcon,
    afterIconSize,
    afterIconClick,
    disabled,
    afterTooltipText,
    afterIconClass,
    autoFocus,
    getOptionLabel,
    options = [],
    label,
    loading,
    clearIcon,
    readOnly,
  } = props;

  const { t } = useTranslation();
  const prevCurrentValues = useRef();
  const [inputValue, setInputValue] = useState("");
  const [defaultVal, setDefaultVal] = useState([]);

  const debouncedGetData = useRef(
    debounce((term) => {
      getData(term);
    }, 300)
  ).current;

  const onInputChange = (event, newInputValue, reason) => {
    setInputValue(newInputValue);

    if (newInputValue.length === 0) return;

    if (reason === "input") {
      if (newInputValue.length >= 3) {
        debouncedGetData(newInputValue);
      }
    }
  };

  useEffect(() => {
    const newVal = get(currentValues, input?.name);
    if (!isEqual(newVal, get(prevCurrentValues.current, input?.name))) {
      setDefaultVal(Array.isArray(newVal) ? newVal : newVal ? [newVal] : []);
    }
    prevCurrentValues.current = currentValues;
  }, [currentValues, input?.name]);

  const isError = () => !!((error || submitError) && touched);

  const safeValue = Array.isArray(defaultVal)
    ? defaultVal
    : defaultVal
    ? [defaultVal]
    : [];

  const handleKeyDown = (event) => {
    if (event.key === "Backspace" && inputValue === "") {
      event.stopPropagation();
    }
  };

  const resolveLabel = (option) => {
    if (!option) return "";
    if (getOptionLabel) {
      const label = getOptionLabel(option);
      return typeof label === "string" ? label : JSON.stringify(label);
    }
    return option.name || option.label || option.value || "";
  };

  return (
    <div className="BMXAsynchronousMulti">
      <Autocomplete
        multiple
        componentsProps={{
          popupIndicator: {
            sx: { display: readOnly ? "none" : undefined },
          },
          clearIndicator: {
            sx: { display: readOnly ? "none" : undefined },
          },
        }}
        disableCloseOnSelect
        freeSolo={false}
        selectOnFocus={false}
        clearOnBlur={false}
        handleHomeEndKeys={false}
        id={`bmx-asynchronous-multi-${input?.name}`}
        options={options}
        name={input?.name}
        loading={loading}
        value={safeValue}
        loadingText={"Ładowanie..."}
        clearIcon={clearIcon}
        inputValue={inputValue}
        onInputChange={onInputChange}
        data-cy="bmx-asynchronous-multi"
        onChange={
          readOnly
            ? undefined
            : (event, newValue, reason) => {
                if (
                  reason === "selectOption" ||
                  reason === "removeOption" ||
                  reason === "clear"
                ) {
                  input.onChange(newValue);
                  setInputValue("");
                } else {
                  setInputValue("");
                }
              }
        }
        getOptionLabel={resolveLabel}
        isOptionEqualToValue={(option, value) =>
          option?.uuid === value?.uuid || option === value
        }
        readOnly={readOnly} //
        onOpen={readOnly ? (e) => e.preventDefault() : undefined}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;

          const handleClick = (event) => {
            event.stopPropagation();
          };

          return (
            <li key={key} {...optionProps} onClick={handleClick}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
                onClick={(event) => {
                  event.stopPropagation();

                  let newValue;
                  if (selected) {
                    newValue = safeValue.filter((v) => v !== option);
                  } else {
                    newValue = [...safeValue, option];
                  }

                  input.onChange(newValue);
                  setDefaultVal(newValue);
                }}
              />
              {resolveLabel(option)}
            </li>
          );
        }}
        renderInput={(params) => (
          <div className="InputArea">
            <TextField
              {...params}
              name={input.name}
              label={label}
              variant="standard"
              autoFocus={autoFocus}
              onKeyDown={handleKeyDown}
              error={isError()}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    {loading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                  </>
                ),
              }}
            />
            {isError() && (
              <FormHelperText>
                <span className="error text">{error || submitError}</span>
              </FormHelperText>
            )}
            {afterIcon && (
              <Tooltip title={afterTooltipText ? t(`${afterTooltipText}`) : ""}>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={afterIconClick}
                  disableRipple={!afterIconClick}
                >
                  <Icon
                    className={`${afterIconClass}`}
                    style={{ fontSize: afterIconSize }}
                  >
                    {afterIcon}
                  </Icon>
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}
      />
    </div>
  );
}

BMXAsynchronousMulti.propTypes = {
  getData: PropTypes.func.isRequired,
  afterTooltipText: PropTypes.string,
  getOptionLabel: PropTypes.func,
  options: PropTypes.array,
  label: PropTypes.string,
};
